<script setup lang="ts">
import type { ClientImageOption } from '~/composables/useCheckoutConfigurator'

/**
 * The customer's own ready disk images, and the blank instance.
 *
 * This tab used to be a card of prose saying "attach an ISO afterwards" — it
 * selected nothing, so a customer with an image already uploaded still had to
 * order a blank instance and rebuild it.
 */
const props = defineProps<{
  images: ClientImageOption[]
  /** The chosen image, or null for the blank instance — an explicit choice. */
  imageId: string | null
}>()

const emit = defineEmits<{ 'update:imageId': [value: string | null] }>()

/** The blank instance's id in the roving order (image ids are UUIDs). */
const BLANK = 'blank-instance'

// Radios owe the radio keyboard contract: one tab stop, arrows inside.
const group = ref<HTMLElement | null>(null)
const roving = useRovingGrid(group, {
  ids: () => [...props.images.map((image) => image.id), BLANK],
  preferred: () => props.imageId ?? BLANK,
})
</script>

<template>
  <div
    ref="group"
    class="space-y-3"
    role="radiogroup"
    :aria-label="$t('billing.page.checkout.os.tabByo')"
    @keydown="roving.onKeydown"
    @focusin="roving.onFocusin"
  >
    <button
      v-for="image in images"
      :key="image.id"
      type="button"
      role="radio"
      :aria-checked="imageId === image.id"
      :data-roving-id="image.id"
      :tabindex="roving.tabStop.value === image.id ? 0 : -1"
      class="flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      :class="imageId === image.id
        ? 'border-primary bg-primary/5 ring-1 ring-primary on-tint'
        : 'border-default hover:border-accented hover:bg-elevated/40'"
      @click="emit('update:imageId', image.id)"
    >
      <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-elevated">
        <UIcon name="i-lucide-hard-drive" class="size-5" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="flex flex-wrap items-center gap-2">
          <span class="truncate text-sm font-semibold">{{ image.name }}</span>
          <UBadge v-if="image.minDiskGb" color="neutral" variant="subtle" size="sm">
            {{ $t('billing.page.checkout.byo.needsDisk', { gb: image.minDiskGb }) }}
          </UBadge>
          <UBadge v-if="!image.hasCloudInit" color="warning" variant="subtle" size="sm">
            {{ $t('billing.page.checkout.byo.noCloudInit') }}
          </UBadge>
          <UBadge
            v-else-if="image.hasCustomCloudInit"
            color="primary"
            variant="subtle"
            size="sm"
          >
            {{ $t('billing.page.checkout.byo.customCloudInit') }}
          </UBadge>
        </span>
        <span class="mt-0.5 block text-xs text-muted">{{ image.sizeLabel }}</span>
      </span>
      <UIcon
        v-if="imageId === image.id"
        name="i-lucide-circle-check"
        class="size-5 shrink-0 text-primary"
      />
    </button>

    <!-- Blank instance: what this tab used to be, now an explicit choice. -->
    <button
      type="button"
      role="radio"
      :aria-checked="imageId === null"
      :data-roving-id="BLANK"
      :tabindex="roving.tabStop.value === BLANK ? 0 : -1"
      class="flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      :class="imageId === null
        ? 'border-primary bg-primary/5 ring-1 ring-primary on-tint'
        : 'border-default hover:border-accented hover:bg-elevated/40'"
      @click="emit('update:imageId', null)"
    >
      <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-elevated">
        <UIcon name="i-lucide-disc-3" class="size-5" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="flex flex-wrap items-center gap-2">
          <span class="text-sm font-semibold">{{ $t('billing.page.checkout.byo.title') }}</span>
          <UBadge color="neutral" variant="subtle" size="sm">
            {{ $t('billing.page.checkout.byo.advanced') }}
          </UBadge>
        </span>
        <span class="mt-1 block text-xs leading-4 text-muted">
          {{ $t('billing.page.checkout.byo.desc') }}
        </span>
      </span>
      <UIcon
        v-if="imageId === null"
        name="i-lucide-circle-check"
        class="size-5 shrink-0 text-primary"
      />
    </button>

  </div>
  <p v-if="!images.length" class="mt-3 text-xs text-muted">
    {{ $t('billing.page.checkout.byo.noImages') }}
  </p>
</template>
